/**
 * useExerciseNotes — reactive, local-only exercise notes (Phase 5).
 *
 * Standalone composable (NOT routed through trainingAdapters): notes are a
 * local-only concern, kept separate from completion / af_pt_plan_checked.
 * Persists to localStorage `af_pt_exercise_notes`; NO Firestore sync this
 * iteration. Pure logic lives in ../lib/exerciseNotes (unit-tested).
 */
import { onMounted, onUnmounted, ref } from 'vue'
import { getActivePlan } from '@/shared/bridge/trainingAdapters'
import { getNoteText, writeNote, type NotesMap, type NoteTarget } from '@/features/training/lib/exerciseNotes'

const NOTES_KEY = 'af_pt_exercise_notes'

function readNotes(): NotesMap {
  try {
    const raw = localStorage.getItem(NOTES_KEY)
    return raw ? (JSON.parse(raw) as NotesMap) : {}
  } catch {
    return {}
  }
}

export function useExerciseNotes() {
  const notes = ref<NotesMap>(readNotes())

  function refresh(): void {
    notes.value = readNotes()
  }

  /** Provenance for new notes (plan name + generated date), best-effort. */
  function currentSourcePlanId(): string | undefined {
    const plan = getActivePlan()
    if (!plan) return undefined
    return [plan.plan_name, plan.generated_at].filter(Boolean).join(' · ') || undefined
  }

  function getNote(target: NoteTarget): string {
    return getNoteText(notes.value, target)
  }

  function setNote(target: NoteTarget, text: string): void {
    const next = writeNote(notes.value, target, text, {
      now: new Date().toISOString(),
      sourcePlanId: currentSourcePlanId(),
    })
    notes.value = next
    try {
      localStorage.setItem(NOTES_KEY, JSON.stringify(next))
    } catch {
      // quota / unavailable — keep UI responsive. Local-only: no Firestore.
    }
  }

  onMounted(() => window.addEventListener('ptr:plan-changed', refresh))
  onUnmounted(() => window.removeEventListener('ptr:plan-changed', refresh))

  return { notes, getNote, setNote, refresh }
}
